/**
 * adpWorkerCrosswalkService — the ADP File # / Co Code crosswalk (Phase 4).
 *
 * Writes the `source_system='adp'` rows of `external_id_map` that
 * `payrollExportService.fetchPayrollCrosswalk` reads. Until these exist, that
 * reader returns nothing, every approved worker comes back as a
 * `no_worker_record` blocker, and the ADP paydata file is empty.
 *
 * ROW SHAPE (must stay identical to `payroll/payrollExportService.ts`, which is
 * the only other reader of these rows):
 *   entity_type   = 'person'
 *   tesseract_id  = public.users.id            -> PayrollWorker.externalId
 *   source_system = 'adp'
 *   external_id   = ADP File #                 -> PayrollWorker.payrollId
 *   source_tenant = ADP Co Code                -> PayrollWorker.payrollTenant
 *
 * ⚠️ SCHEMA TRAP: migration 00387 declares `unique (source_system, entity_type,
 * external_id)` — WITHOUT `source_tenant`, which only arrived in 00413. So two
 * associates in different Co Codes who happen to share a File # cannot both be
 * stored. `parseAdpWorkerCsv` + `detectCrossTenantFileNumberCollisions` catch
 * that before it reaches Postgres; fixing it properly needs a migration that
 * widens the constraint (logged as open in BUILD_LOG Phase 4).
 *
 * The pure parser is `lib/adpWorkerImport.ts`. No UI here — Phase 5 wires it up.
 */
import { supabase } from '@/lib/supabase'
import { IS_LIVE_SAP } from '@/config/sapBackend'
import type { AdpWorkerRow } from '../lib/adpWorkerImport'

/** The ADP crosswalk row as the app sees it. */
export interface AdpWorkerCrosswalkEntry {
  /** `external_id_map.id`. */
  id: string
  /** `tesseract_id` — public.users.id. */
  employeeId: string
  /** `external_id` — the ADP File #. */
  fileNumber: string
  /** `source_tenant` — the ADP Co Code. Null = untagged / single-tenant. */
  coCode: string | null
  updatedAt: string | null
}

/** What an upsert needs. `AdpWorkerRow` from the parser satisfies this. */
export type AdpWorkerCrosswalkInput = Pick<AdpWorkerRow, 'employeeId' | 'fileNumber' | 'coCode'>

type DbExternalIdRow = {
  id: string
  tesseract_id: string
  external_id: string
  source_tenant: string | null
  updated_at: string | null
}

const COLS = 'id, tesseract_id, external_id, source_tenant, updated_at'

const ENTITY_TYPE = 'person'
const SOURCE_SYSTEM = 'adp'
/** Mirrors the 00387 comment's convention ('sap_employee_no', 'adp_employee_id'). */
const EXTERNAL_ID_FIELD = 'adp_file_number'

/** PostgREST rejects very large payloads; the importer chunks at this size. */
const UPSERT_CHUNK = 500

function dbToApp(row: DbExternalIdRow): AdpWorkerCrosswalkEntry {
  return {
    id: row.id,
    employeeId: row.tesseract_id,
    fileNumber: row.external_id,
    coCode: row.source_tenant ?? null,
    updatedAt: row.updated_at ?? null,
  }
}

/** Every ADP person crosswalk row, Co Code then File # ordered. */
export async function listAdpWorkerCrosswalk(): Promise<AdpWorkerCrosswalkEntry[]> {
  const { data, error } = await supabase
    .from('external_id_map')
    .select(COLS)
    .eq('entity_type', ENTITY_TYPE)
    .eq('source_system', SOURCE_SYSTEM)
    .order('source_tenant', { ascending: true })
    .order('external_id', { ascending: true })
  if (error) throw new Error(`list external_id_map failed: ${error.message}`)
  return (data ?? []).map((r) => dbToApp(r as DbExternalIdRow))
}

/**
 * Insert-or-update the ADP crosswalk rows for a set of employees.
 *
 * Conflict target is `(entity_type, tesseract_id, source_system)` — one ADP id
 * per person — so re-importing a corrected file UPDATES in place rather than
 * minting duplicates. Reassigning a File # that already belongs to someone else
 * violates the other unique index and fails loudly, which is correct: a File #
 * cannot belong to two people.
 *
 * Returns how many rows were sent. Chunked so a several-thousand-row import
 * does not hit the PostgREST payload ceiling.
 */
export async function upsertAdpWorkerCrosswalk(
  rows: AdpWorkerCrosswalkInput[],
): Promise<{ upserted: number }> {
  if (rows.length === 0) return { upserted: 0 }
  const now = new Date().toISOString()
  const payload = rows.map((r) => ({
    // S/4 build: the config-store shim ignores `onConflict` and keys a row by
    // `id`, else by a composite that INCLUDES the File #. Without a stable id a
    // corrected File # would land as a second row next to the old one, and the
    // payroll file could pick either. One row per (person, adp), as in Postgres,
    // where `id` is the uuid PK and must not be sent.
    ...(IS_LIVE_SAP ? { id: `${ENTITY_TYPE}|${r.employeeId}|${SOURCE_SYSTEM}` } : {}),
    entity_type: ENTITY_TYPE,
    tesseract_id: r.employeeId,
    source_system: SOURCE_SYSTEM,
    external_id: r.fileNumber,
    external_id_field: EXTERNAL_ID_FIELD,
    source_tenant: r.coCode || null,
    updated_at: now,
  }))

  for (let i = 0; i < payload.length; i += UPSERT_CHUNK) {
    const chunk = payload.slice(i, i + UPSERT_CHUNK)
    const { error } = await supabase
      .from('external_id_map')
      .upsert(chunk, { onConflict: 'entity_type,tesseract_id,source_system' })
    if (error) throw new Error(`upsert external_id_map failed: ${error.message}`)
  }
  return { upserted: payload.length }
}

/** Remove one crosswalk row by `external_id_map.id`. */
export async function deleteAdpWorkerCrosswalk(id: string): Promise<void> {
  const { error } = await supabase.from('external_id_map').delete().eq('id', id)
  if (error) throw new Error(`delete external_id_map failed: ${error.message}`)
}
